import type { UserStatusHydraItem } from "@verbleif/lib/src/composables/Api/Type/UserStatus/user_status.interface";
import type { CrudArgs } from "./composables/ContextVoter";
import type { AggregateVoterCheck, ContextVoterInterface } from "./composables/ContextVoter.interface";
import type { ParentTaskEntityVote, TaskAssigneeEntityVote, TaskEntityVote, TaskUserEntityVote, TaskUserGroupEntityVote } from "./voters.helpers";
import { PermissionScope } from "@verbleif/lib/src/composables/Api/Type/UserPermissions/userPermission.enum";
import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { ContextVoter } from "./composables/ContextVoter";

export type PermissionExpression
  = | PermissionAttributesEnum // single attribute
    | AndExpression
    | OrExpression;

export interface AndExpression {
  type: "and"
  expressions: PermissionExpression[]
}

export interface OrExpression {
  type: "or"
  expressions: PermissionExpression[]
}

// Helper functions for building permission expressions
export function and(...expressions: PermissionExpression[]): AndExpression {
  return { type: "and", expressions };
}

export function or(...expressions: PermissionExpression[]): OrExpression {
  return { type: "or", expressions };
}

const createContextAttributes = [
  PermissionAttributesEnum.ContextTaskCreateAssigneddirectlytoscope,
  PermissionAttributesEnum.ContextTaskCreateForassignedusergrouplessuser,
  PermissionAttributesEnum.ContextTaskCreateAssignedtoothersinscope,
  PermissionAttributesEnum.ContextTaskCreateunassigned,
];

const readContextAttributes = [
  PermissionAttributesEnum.ContextTaskReadAssigneddirectlytoscope,
  PermissionAttributesEnum.ContextTaskReadOfassignedusergrouplessuser,
  PermissionAttributesEnum.ContextTaskReadAssignedofothersinscope,
  PermissionAttributesEnum.ContextTaskReadUnassigned,
];

const deleteContextAttributes = [
  PermissionAttributesEnum.ContextTaskDeleteAssigneddirectlytoscope,
  PermissionAttributesEnum.ContextTaskDeleteForassignedusergrouplessuser,
  PermissionAttributesEnum.ContextTaskDeleteAssignedtoothersinscope,
  PermissionAttributesEnum.ContextTaskDeleteUnassigned,
];

const updateContextAttributes = [
  PermissionAttributesEnum.ContextTaskUpdateAssigneddirectlytoscope,
  PermissionAttributesEnum.ContextTaskUpdateForassignedusergrouplessuser,
  PermissionAttributesEnum.ContextTaskUpdateAssignedtoothersinscope,
  PermissionAttributesEnum.ContextTaskUpdateunassigned,
];

/**
 * The six role attributes of one task action. The API votes on each of them in its own
 * Task*StrategyVoter and every strategy only applies in its own assignment situation, so
 * the web voter has to do the same instead of OR-ing every attribute at every scope.
 */
interface TaskActionAttributes {
  global: PermissionAttributesEnum
  scope: PermissionAttributesEnum
  userGroupLess: PermissionAttributesEnum
  others: PermissionAttributesEnum
  unassigned: PermissionAttributesEnum
  self: PermissionAttributesEnum
  createdBySelf: PermissionAttributesEnum
}

const updateActionAttributes: TaskActionAttributes = {
  global: PermissionAttributesEnum.GlobalOperationTaskUpdateany,
  scope: PermissionAttributesEnum.ContextTaskUpdateAssigneddirectlytoscope,
  userGroupLess: PermissionAttributesEnum.ContextTaskUpdateForassignedusergrouplessuser,
  others: PermissionAttributesEnum.ContextTaskUpdateAssignedtoothersinscope,
  unassigned: PermissionAttributesEnum.ContextTaskUpdateunassigned,
  self: PermissionAttributesEnum.ContextTaskUpdateAssigneddirectlytoself,
  createdBySelf: PermissionAttributesEnum.ContextTaskUpdateUpdatecreatedbyself,
};

const deleteActionAttributes: TaskActionAttributes = {
  global: PermissionAttributesEnum.GlobalOperationTaskDeleteany,
  scope: PermissionAttributesEnum.ContextTaskDeleteAssigneddirectlytoscope,
  userGroupLess: PermissionAttributesEnum.ContextTaskDeleteForassignedusergrouplessuser,
  others: PermissionAttributesEnum.ContextTaskDeleteAssignedtoothersinscope,
  unassigned: PermissionAttributesEnum.ContextTaskDeleteUnassigned,
  self: PermissionAttributesEnum.ContextTaskDeleteAssigneddirectlytoself,
  createdBySelf: PermissionAttributesEnum.ContextTaskDeleteDeletecreatedbyself,
};

interface TaskVoterInterface
  extends Omit<ContextVoterInterface, "canUpdate" | "canDelete"> { }

export class TaskVoter extends ContextVoter implements TaskVoterInterface {
  public canCreate: ContextVoterInterface["canCreate"] = (item) => {
    return this._canCreate({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationTaskCreateany,
      contextAttribute: [
        ...createContextAttributes,
        PermissionAttributesEnum.ContextTaskCreateAssigneddirectlytoself,
      ],
    });
  };

  public canRead: ContextVoterInterface["canRead"] = (item) => {
    return this._canCrud({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationTaskReadany,
      contextAttribute: readContextAttributes,
    });
  };

  public canReadTasks = (
    type: "userGroupUser" | "userGroup" | "userGroupLessUser",
    item: CrudArgs = {
      location: this.permissionService.defaultLocation,
      client: this.permissionService.defaultClient,
    },
  ): boolean => {
    let contextAttributes: PermissionAttributesEnum[] = [];
    let scope: Exclude<PermissionScope, PermissionScope.GLOBAL> | undefined;

    if (type === "userGroupUser") {
      contextAttributes = [
        PermissionAttributesEnum.ContextTaskReadAssignedofothersinscope,
      ];
      scope = PermissionScope.USER_GROUP;
    } else if (type === "userGroup") {
      contextAttributes = [
        PermissionAttributesEnum.ContextTaskReadAssigneddirectlytoscope,
      ];
      scope = PermissionScope.USER_GROUP;
    } else if (type === "userGroupLessUser") {
      contextAttributes = [
        PermissionAttributesEnum.ContextTaskReadOfassignedusergrouplessuser,
      ];
    }

    if (contextAttributes.length === 0) {
      return false;
    }

    if (
      this._canCrud({
        item,
        globalAttribute: PermissionAttributesEnum.GlobalOperationTaskReadany,
        contextAttribute: contextAttributes,
        scope,
      })
    ) {
      return true;
    }
    return false;
  };

  public canDelete = (item: TaskEntityVote): boolean => {
    return this._canDeleteOrUpdate(item, deleteActionAttributes);
  };

  /**
   * Mirrors the API: a task may be updated directly, or (sub-task) when the user may update its parent.
   * The parent is judged with the same logic on its own assignees, creator, property and location,
   * one level only. There is no via-parent rule for delete in the API.
   */
  public canUpdate(item: TaskEntityVote): boolean {
    return this._canDeleteOrUpdate(item, updateActionAttributes)
      || (!!item.parent && this._canDeleteOrUpdate(item.parent, updateActionAttributes));
  }

  /**
   * Whether the user holds an update right in any scope (global, client, location, property, user group,
   * own task, created by self), without judging a specific task. Callers use it as the safe fallback when
   * a sub-task's parent is not available: they show the update action and the API decides.
   */
  public canUpdateAnywhere(item: CrudArgs = {
    location: this.permissionService.defaultLocation,
    client: this.permissionService.defaultClient,
  }): boolean {
    return this._canCrud({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationTaskUpdateany,
      contextAttribute: [
        ...updateContextAttributes,
        PermissionAttributesEnum.ContextTaskUpdateAssigneddirectlytoself,
        PermissionAttributesEnum.ContextTaskUpdateUpdatecreatedbyself,
      ],
    });
  }

  /**
   * Whether the user may touch tasks without assignees, regardless of the assignees the given
   * item currently has (it answers "may this task end up unassigned").
   */
  public hasAccessToUnassigned(item: TaskEntityVote): boolean {
    const isCreate = !item["@id"];
    return this._canCrud({
      item,
      globalAttribute: isCreate
        ? PermissionAttributesEnum.GlobalOperationTaskCreateany
        : PermissionAttributesEnum.GlobalOperationTaskUpdateany,
      contextAttribute: isCreate
        ? PermissionAttributesEnum.ContextTaskCreateunassigned
        : PermissionAttributesEnum.ContextTaskUpdateunassigned,
    }) || this._hasPropertyAccess(
      item,
      isCreate
        ? PermissionAttributesEnum.ContextTaskCreateunassigned
        : PermissionAttributesEnum.ContextTaskUpdateunassigned,
    );
  }

  private _hasPropertyAccess(item: ParentTaskEntityVote, attribute: PermissionAttributesEnum): boolean {
    return !!item.property
      && this.permissionService.getScopePermissionForUser({
        iri: item.property,
        attribute,
        scope: PermissionScope.PROPERTY,
      }) !== null;
  }

  /** Client or location level grant of a single attribute (the property level is checked apart). */
  private _hasClientOrLocationAccess(item: ParentTaskEntityVote, attribute: PermissionAttributesEnum): boolean {
    return this.hasAccess({
      location: item.location,
      attribute,
    });
  }

  /**
   * Mirrors the API: TaskVoter::canUpdate / canDelete hand the task to the Task*StrategyVoters, and
   * every strategy only applies in its own assignment situation. A global `any` wins before that.
   */
  private _canDeleteOrUpdate(
    item: ParentTaskEntityVote,
    attributes: TaskActionAttributes,
  ): boolean {
    if (this.permissionService.getGlobalPermissionForUser({ attribute: attributes.global })) {
      return true;
    }

    const assignees = item.assignees ?? [];
    const users = assignees.filter(assignee => assignee.includes("/users/"));
    const userGroups = assignees.filter(assignee => assignee.includes("/user_groups/"));
    const userIri = this.permissionService.userIri;
    const isAssigned = assignees.length > 0;
    const isAssignedToSelf = !!userIri && users.includes(userIri);
    const isCreatedBySelf = !!userIri && item.createdBy === userIri;

    // TaskUnassignedStrategyVoter: the only strategy that applies to a task without assignees.
    if (!isAssigned) {
      if (this._hasClientOrLocationAccess(item, attributes.unassigned) || this._hasPropertyAccess(item, attributes.unassigned)) {
        return true;
      }
    }

    // TaskDirectlyToScopeStrategyVoter: assigned tasks only; client, location, property or an assigned user group.
    if (isAssigned && this._hasDirectAccess(item, attributes.scope, userGroups)) {
      return true;
    }

    // TaskAssignedDirectlyToSelfStrategyVoter: the current user is one of the assigned users.
    if (isAssignedToSelf && this._hasDirectAccess(item, attributes.self, userGroups)) {
      return true;
    }

    // TaskCreatedBySelfStrategyVoter: created by the current user, in any assignment state.
    if (isCreatedBySelf && this._hasCreatedBySelfAccess(item, attributes.createdBySelf, userIri)) {
      return true;
    }

    // TaskUserGroupLessUserStrategyVoter: client or location level, for at least one assigned user without user group.
    if (users.length > 0 && this._hasUserGroupLessAccess(item, attributes.userGroupLess, users)) {
      return true;
    }

    // TaskAssignedOfOthersInScopeStrategyVoter: user group or property scope covering at least one assigned user.
    return users.length > 0 && this._hasOthersInScopeAccess(item, attributes.others, users);
  }

  private _hasDirectAccess(item: ParentTaskEntityVote, attribute: PermissionAttributesEnum, userGroups: string[]): boolean {
    if (this._hasClientOrLocationAccess(item, attribute) || this._hasPropertyAccess(item, attribute)) {
      return true;
    }

    return userGroups.some(userGroup => this.permissionService.getScopePermissionForUser({
      iri: userGroup,
      attribute,
      scope: PermissionScope.USER_GROUP,
    }) !== null);
  }

  private _hasCreatedBySelfAccess(item: ParentTaskEntityVote, attribute: PermissionAttributesEnum, userIri: string): boolean {
    if (this._hasClientOrLocationAccess(item, attribute) || this._hasPropertyAccess(item, attribute)) {
      return true;
    }

    const ownUserGroups = (this.permissionService.getUsersData().get(userIri)?.statuses ?? [])
      .filter(status => status.scope === PermissionScope.USER_GROUP && status.location === item.location)
      .map(status => status.userGroup)
      .filter((userGroup): userGroup is string => !!userGroup);
    if (ownUserGroups.length === 0) {
      return false;
    }

    const userGroupsWithAttribute = this.permissionService.getAllIdentitiesViaScope({
      scope: PermissionScope.USER_GROUP,
      attribute,
      column: "userGroup",
      mode: "every",
    });

    return ownUserGroups.some(userGroup => userGroupsWithAttribute.includes(userGroup));
  }

  private _hasUserGroupLessAccess(item: ParentTaskEntityVote, attribute: PermissionAttributesEnum, users: string[]): boolean {
    if (!this._hasClientOrLocationAccess(item, attribute)) {
      return false;
    }

    // A user that is not in the loaded user data cannot be shown to be user group less.
    const usersData = this.permissionService.getUsersData();
    return users.some((user) => {
      const statuses = usersData.get(user)?.statuses;
      return !!statuses && !statuses.some(
        (status: UserStatusHydraItem) =>
          status.location === item.location && status.scope === PermissionScope.USER_GROUP,
      );
    });
  }

  private _hasOthersInScopeAccess(item: ParentTaskEntityVote, attribute: PermissionAttributesEnum, users: string[]): boolean {
    const userGroupsWithAttribute = this.permissionService.getAllIdentitiesViaScope({
      scope: PermissionScope.USER_GROUP,
      attribute,
      column: "userGroup",
      mode: "every",
    });
    const usersInUserGroups = this.permissionService.getUsersInUserGroups(userGroupsWithAttribute);
    if (users.some(user => usersInUserGroups.includes(user))) {
      return true;
    }

    if (!item.property) {
      return false;
    }

    const propertiesWithAttribute = this.permissionService.getAllIdentitiesViaScope({
      scope: PermissionScope.PROPERTY,
      attribute,
      column: "property",
      mode: "every",
    });
    if (!propertiesWithAttribute.includes(item.property)) {
      return false;
    }

    const usersInProperties = this.permissionService.getUsersInProperties(propertiesWithAttribute);
    return users.some(user => usersInProperties.includes(user));
  }

  public canCrud: AggregateVoterCheck = (
    item = {
      client: this.permissionService.defaultClient,
      location: this.permissionService.defaultLocation,
    },
  ) => {
    return this._canCrud({
      item,
      globalAttribute: [
        PermissionAttributesEnum.GlobalOperationTaskCreateany,
        PermissionAttributesEnum.GlobalOperationTaskReadany,
        PermissionAttributesEnum.GlobalOperationTaskUpdateany,
        PermissionAttributesEnum.GlobalOperationTaskDeleteany,
      ],
      contextAttribute: [
        ...createContextAttributes,
        PermissionAttributesEnum.ContextTaskCreateAssigneddirectlytoself,
        ...readContextAttributes,
        ...updateContextAttributes,
        PermissionAttributesEnum.ContextTaskUpdateAssigneddirectlytoself,
        ...deleteContextAttributes,
        PermissionAttributesEnum.ContextTaskDeleteAssigneddirectlytoself,
      ],
    });
  };

  public canAssignAny(): boolean {
    return !!this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalOperationTaskassigneeCreateany,
    });
  }

  /**
   * Checks if the user has permission to assign a task to an assignee.
   *
   * This method verifies if the user has the global permission to create any task assignee.
   *
   * @param taskEntity - The task entity to which the task is to be assigned
   * @param assignee - The assignee entity to which the task is to be assigned
   * @returns {boolean} - Returns true if the user has the permission to assign a task to the assignee, false otherwise.
   */
  public canAssign(
    taskEntity: TaskEntityVote,
    assignee: TaskAssigneeEntityVote,
  ): boolean {
    if (this.canAssignAny()) {
      return true;
    }
    const isCreatedBySelf = taskEntity.createdBy === this.permissionService.userIri;
    const userIri = this.permissionService.userIri;
    const isSelfAssigneed = userIri !== null && taskEntity.assignees?.includes(userIri);

    if (assignee["@type"] === "User") {
      const userIsSelf = assignee["@id"] === this.permissionService.userIri;

      return this.hasAccessToUser({
        taskEntity,
        assignee,
        attribute: taskEntity?.["@id"]
          ? [
              PermissionAttributesEnum.ContextTaskUpdateAssigneddirectlytoscope,
              PermissionAttributesEnum.ContextTaskassigneeAssignToothersinscope,
            ]
          : [
              PermissionAttributesEnum.ContextTaskCreateAssigneddirectlytoscope,
              PermissionAttributesEnum.ContextTaskassigneeAssignToothersinscope,
            ],
        directlyToSelfAttribute: userIsSelf
          ? taskEntity?.["@id"]
            ? PermissionAttributesEnum.ContextTaskUpdateAssigneddirectlytoself
            : PermissionAttributesEnum.ContextTaskCreateAssigneddirectlytoself
          : undefined,
        userGroupLessUserAttribute:
          PermissionAttributesEnum.ContextTaskassigneeAssignUsergrouplessuser,
        propertyAttribute:
          PermissionAttributesEnum.ContextTaskassigneeAssignToothersinscope,
      });
    }

    if (assignee["@type"] === "UserGroup") {
      const isUpdate = !!taskEntity?.["@id"];
      const commonPermission = PermissionAttributesEnum.ContextTaskassigneeAssignUsergroup;

      // Build array of permission alternatives
      const alternatives: PermissionAttributesEnum[] = [
        isUpdate
          ? PermissionAttributesEnum.ContextTaskUpdateAssigneddirectlytoscope
          : PermissionAttributesEnum.ContextTaskCreateAssigneddirectlytoscope,
      ];

      if (isSelfAssigneed) {
        alternatives.push(
          isUpdate
            ? PermissionAttributesEnum.ContextTaskUpdateAssigneddirectlytoself
            : PermissionAttributesEnum.ContextTaskCreateAssigneddirectlytoself,
        );
      }

      if (isCreatedBySelf && isUpdate) {
        alternatives.push(PermissionAttributesEnum.ContextTaskUpdateUpdatecreatedbyself);
      }

      // Create OR of all alternatives, each combined with the common permission
      const attributes = or(
        ...alternatives.map(alt => and(alt, commonPermission)),
      );

      return this.hasAccessToUserGroup({
        taskEntity,
        assignee,
        attribute: attributes,
      });
    }

    return false;
  }

  private hasAccessToUser({
    taskEntity,
    assignee,
    attribute,
    userGroupLessUserAttribute,
    propertyAttribute,
    directlyToSelfAttribute,
  }: {
    taskEntity: TaskEntityVote
    assignee: TaskUserEntityVote
    attribute: PermissionAttributesEnum | PermissionAttributesEnum[]
    userGroupLessUserAttribute: PermissionAttributesEnum
    propertyAttribute: PermissionAttributesEnum
    directlyToSelfAttribute?: PermissionAttributesEnum | PermissionAttributesEnum[]
  }): boolean {
    const location = taskEntity.location;
    const client = location
      ? this.permissionService.getClientViaLocation(location)
      : null;
    if (client === null) {
      return false;
    }

    if (this.permissionService.hasAccessVia({ iri: client, attribute })) {
      return true;
    }

    if (this.permissionService.hasAccessVia({ iri: location, attribute })) {
      return true;
    }

    const isUserGroupLess = !assignee.statuses.some(
      (status: UserStatusHydraItem) =>
        status.location === location
        && status.scope === PermissionScope.USER_GROUP,
    );

    if (
      isUserGroupLess
      && this.permissionService.hasAccessVia({
        iri: client,
        attribute: userGroupLessUserAttribute,
      })
    ) {
      return true;
    }

    if (
      isUserGroupLess
      && this.permissionService.hasAccessVia({
        iri: location,
        attribute: userGroupLessUserAttribute,
      })
    ) {
      return true;
    }

    const explicitUserGroupsAllowed
      = this.permissionService.getAllIdentitiesViaScope({
        scope: PermissionScope.USER_GROUP,
        attribute,
        column: "userGroup",
        mode: "every",
      });

    const implicitUserGroupUserIds
      = this.permissionService.getUsersInUserGroups(explicitUserGroupsAllowed);

    if (implicitUserGroupUserIds.includes(assignee["@id"])) {
      return true;
    }

    const property = taskEntity.property;

    if (directlyToSelfAttribute) {
      if (this.permissionService.hasAccessVia({ iri: client, attribute: directlyToSelfAttribute })) {
        return true;
      }

      if (this.permissionService.hasAccessVia({ iri: location, attribute: directlyToSelfAttribute })) {
        return true;
      }
      const userGroups = taskEntity.assignees.filter(assignee =>
        assignee.includes("/user_groups/"),
      );

      if (property) {
        if (this.permissionService.hasAccessVia({ iri: property, attribute: propertyAttribute })) {
          return true;
        }
      }

      for (const userGroup of userGroups) {
        if (this.permissionService.hasAccessVia({ iri: userGroup, attribute: directlyToSelfAttribute })) {
          return true;
        }
      }
    }

    if (property) {
      const explicitPropertiesAllowed
        = this.permissionService.getAllIdentitiesViaScope({
          scope: PermissionScope.PROPERTY,
          attribute: propertyAttribute,
          column: "property",
          mode: "every",
        });

      if (!explicitPropertiesAllowed.includes(property)) {
        return false;
      }

      const implicitPropertyUserIds
        = this.permissionService.getUsersInProperties(explicitPropertiesAllowed);
      if (implicitPropertyUserIds.includes(assignee["@id"])) {
        return true;
      }
    }

    return false;
  }

  /**
   * Determines if the current user has the necessary permissions to assign a task to a specified user group.
   *
   * @param {object} params - The parameters for the function.
   * @param {TaskEntityVote} params.taskEntity - The task entity that is being assigned.
   * @param {TaskUserGroupEntityVote} params.assignee - The user group entity to which the task is being assigned.
   * @param {PermissionExpression | PermissionAttributesEnum[]} params.attribute - The permission expression to verify.
   *   - Single attribute: Direct permission check
   *   - Array of attributes: All must be true (AND logic) - backward compatibility
   *   - PermissionExpression: Use and() and or() helpers for complex logic
   * @returns {boolean} Returns true if the user has the required permission to assign the task to the user group, otherwise returns false.
   */
  private hasAccessToUserGroup({
    taskEntity,
    assignee,
    attribute,
  }: {
    taskEntity: TaskEntityVote
    assignee: TaskUserGroupEntityVote
    attribute: PermissionExpression | PermissionAttributesEnum[] // Legacy array support
  }): boolean {
    // Check if location exists
    const location = taskEntity.location;
    if (!location) {
      console.error("Location is required");
      return false;
    }

    // Get client for the location
    const client = this.permissionService.getClientViaLocation(location);
    if (!client) {
      return false;
    }

    // Helper function to check if a single attribute has permission
    const hasPermissionForAttribute = (attr: PermissionAttributesEnum): boolean => {
      // First check if the user group has the permission on the client
      const clientPermission = this.permissionService.getClientPermissionForUser({
        clientIri: client,
        attribute: attr,
      });
      if (clientPermission !== null) {
        return true;
      }

      // Then check if the user group has the permission on the location
      const userLocationPermission = this.permissionService.getLocationPermissionForUser({
        locationIri: location,
        attribute: attr,
      });
      if (userLocationPermission !== null) {
        return true;
      }

      // Then check if the user group has the permission directly on the user group
      const userPermission = this.permissionService.getScopePermissionForUser({
        iri: assignee["@id"],
        attribute: attr,
        scope: PermissionScope.USER_GROUP,
      });
      return userPermission !== null;
    };

    // Recursive function to evaluate permission expressions
    function evaluateExpression(expr: PermissionExpression): boolean {
      if (typeof expr === "string") {
        // Single attribute
        return hasPermissionForAttribute(expr);
      }

      if (expr.type === "and") {
        // AND logic: all expressions must be true
        return expr.expressions.every(evaluateExpression);
      }

      if (expr.type === "or") {
        // OR logic: at least one expression must be true
        return expr.expressions.some(evaluateExpression);
      }

      return false;
    }

    // Handle legacy array format (backward compatibility)
    if (Array.isArray(attribute)) {
      // Legacy array of PermissionAttributesEnum - use AND logic
      return attribute.every(hasPermissionForAttribute);
    }

    // Handle new PermissionExpression format
    return evaluateExpression(attribute);
  }

  public canUnassignAny(): boolean {
    // return false;
    return !!this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalOperationTaskassigneeDeleteany,
    });
  }

  /**
   * Checks if the user has permission to unassign a task from an assignee.
   *
   * This method verifies if the user has the global permission to delete any task assignee.
   *
   * @param taskEntity - The task entity to which the task is to be unassigned
   * @param assignee - The assignee entity from which the task is to be unassigned
   * @returns {boolean} - Returns true if the user has the permission to unassign a task from the assignee, false otherwise.
   */
  public canUnassign(
    taskEntity: TaskEntityVote,
    assignee: TaskAssigneeEntityVote,
  ): boolean {
    // return false;
    if (this.canUnassignAny()) {
      return true;
    }

    if (assignee["@type"] === "User") {
      return this.hasAccessToUser({
        taskEntity,
        assignee,
        attribute:
          PermissionAttributesEnum.ContextTaskassigneeUnassignOthersinscope,
        userGroupLessUserAttribute:
          PermissionAttributesEnum.ContextTaskassigneeUnassignUsergrouplessusers,
        propertyAttribute:
          PermissionAttributesEnum.ContextTaskassigneeUnassignOthersinscope,
      });
    }

    if (assignee["@type"] === "UserGroup") {
      return this.hasAccessToUserGroup({
        taskEntity,
        assignee,
        attribute:
          PermissionAttributesEnum.ContextTaskassigneeUnassignOthersinscope,
      });
    }

    return false;
  }

  public hasAccessToAttributeViaTaskRelations(
    item: TaskEntityVote,
    attribute: PermissionAttributesEnum,
  ) {
    const location = item.location;
    const property = item.property;
    const client = location
      ? this.permissionService.getClientViaLocation(location)
      : null;
    const users = item.assignees
      ? item.assignees.filter(assignee =>
          assignee.includes("/users/"),
        )
      : [];
    const userGroups = item.assignees
      ? item.assignees.filter(assignee =>
          assignee.includes("/user_groups/"),
        )
      : [];

    if (client) {
      const userPermission = this.permissionService.getScopePermissionForUser({
        iri: client,
        attribute,
        scope: PermissionScope.CLIENT,
      });
      if (userPermission !== null) {
        return true;
      }
    }

    if (location) {
      const userLocationPermission
        = this.permissionService.getScopePermissionForUser({
          iri: location,
          attribute,
          scope: PermissionScope.LOCATION,
        });
      if (userLocationPermission !== null) {
        return true;
      }
    }

    if (property) {
      const userPropertyPermission
        = this.permissionService.getScopePermissionForUser({
          iri: property,
          attribute,
          scope: PermissionScope.PROPERTY,
        });
      if (userPropertyPermission !== null) {
        return true;
      }
    }

    if (userGroups.length > 0) {
      for (const userGroup of userGroups) {
        const userGroupPermission
          = this.permissionService.getScopePermissionForUser({
            iri: userGroup,
            attribute,
            scope: PermissionScope.USER_GROUP,
          });
        if (userGroupPermission !== null) {
          return true;
        }
      }
    }

    const usersData = this.permissionService.getUsersData();

    const userStatuses = users
      .map(user => usersData.get(user)?.statuses || null)
      .filter((status): status is UserStatusHydraItem[] => status !== null);

    // Return array of unique strings with all userGroupIris
    const userUserGroupStatuses = Array.from(
      new Set(
        userStatuses
          .flatMap(status =>
            status.filter(s => !!s.userGroup).map(s => s.userGroup),
          )
          .filter((userGroup): userGroup is string => userGroup !== undefined),
      ),
    );

    if (userUserGroupStatuses.length > 0) {
      for (const userGroup of userUserGroupStatuses) {
        const userGroupPermission
          = this.permissionService.getScopePermissionForUser({
            iri: userGroup,
            attribute,
            scope: PermissionScope.USER_GROUP,
          });
        if (userGroupPermission !== null) {
          return true;
        }
      }
    }

    return false;
  }
}
